import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useMutation } from '@apollo/client'
import { ArrowLeftIcon } from '@heroicons/react/24/outline'
import { CREATE_RAW_MATERIAL } from '../../graphql/costing-mutations'
import { GET_RAW_MATERIALS } from '../../graphql/costing-queries'
import { unitLabel } from '../../lib/costingUtils'
import toast from 'react-hot-toast'

const UNITS = ['g', 'kg', 'ml', 'l', 'piece'] as const

const CreateRecipePage = () => {
  const navigate = useNavigate()

  const [name, setName] = useState('')
  const [unit, setUnit] = useState<typeof UNITS[number]>('ml')
  const [outputQuantity, setOutputQuantity] = useState('')
  const [notes, setNotes] = useState('')

  const [createRawMaterial, { loading }] = useMutation(CREATE_RAW_MATERIAL, {
    refetchQueries: [{ query: GET_RAW_MATERIALS, variables: { filters: { type: 'recipe' } } }],
    onCompleted: (data) => {
      toast.success(`"${data.createRawMaterial.name}" created`)
      navigate(`/costing/recipes/${data.createRawMaterial.id}`)
    },
    onError: (err) => {
      toast.error(`Failed to create: ${err.message}`)
    },
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!name.trim()) {
      toast.error('Name is required')
      return
    }
    const qty = parseFloat(outputQuantity)
    if (isNaN(qty) || qty <= 0) {
      toast.error('Output quantity must be greater than 0')
      return
    }
    createRawMaterial({
      variables: {
        data: {
          name: name.trim(),
          unit,
          type: 'recipe',
          output_quantity: qty,
          notes: notes.trim() || undefined,
        },
      },
    })
  }

  return (
    <div className="space-y-6 max-w-2xl">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <Link
          to="/costing/recipes"
          className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100"
        >
          <ArrowLeftIcon className="h-5 w-5" />
        </Link>
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">New Recipe</h1>
          <p className="mt-1 text-sm text-gray-500">
            Create a recipe whose cost is calculated from its ingredients.
          </p>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="card p-6 space-y-5">
        {/* Name */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Name <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Curry Sauce"
            className="input w-full"
            required
          />
        </div>

        {/* Unit */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Unit <span className="text-red-500">*</span>
          </label>
          <select
            value={unit}
            onChange={(e) => setUnit(e.target.value as typeof UNITS[number])}
            className="input w-full"
          >
            {UNITS.map((u) => (
              <option key={u} value={u}>
                {unitLabel(u)}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-gray-500">
            The unit for the recipe output. Cannot be changed after creation.
          </p>
        </div>

        {/* Output quantity */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            This recipe produces <span className="text-red-500">*</span>
          </label>
          <div className="flex items-center gap-2">
            <input
              type="number"
              min="0.001"
              step="any"
              value={outputQuantity}
              onChange={(e) => setOutputQuantity(e.target.value)}
              placeholder="e.g. 750"
              className="input w-40"
              required
            />
            <span className="text-sm text-gray-600 font-medium">{unit}</span>
          </div>
          <p className="mt-1 text-xs text-gray-500">
            The actual measured output of one batch. Cost per {unit} = total ingredient cost ÷ this quantity.
          </p>
        </div>

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Optional notes about this recipe"
            rows={3}
            className="input w-full"
          />
        </div>

        {/* Actions */}
        <div className="flex items-center gap-3 pt-2">
          <button type="submit" disabled={loading} className="btn-primary">
            {loading ? 'Creating…' : 'Create Recipe'}
          </button>
          <Link to="/costing/recipes" className="btn-secondary">
            Cancel
          </Link>
        </div>
      </form>
    </div>
  )
}

export default CreateRecipePage
